import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { Table, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { FiEdit2, FiTrash2, FiPlus, FiRepeat, FiFlag, FiX, FiGlobe, FiMapPin } from 'react-icons/fi';
import * as slotSettingActions from '../../Redux/Actions/slotSettings';
import { toastMessage } from '../../utils/toast';
import DateOverrideFormModal from './dateOverrideFormModal';
import ConfirmModal from './confirmModal';
import { SectionCard, ErrorState, EmptyState, AlertBox } from './common';
import { CenterSelect, DateRangeField } from './fields';
import { DATE_OVERRIDE_TYPE, dateOverrideLabel, toCenterId, getErrorMessage, formatDate, formatTimeRange } from './helpers';

const defaultFilter = () => ({
    centerId: '',
    from: dayjs().startOf('month').format('YYYY-MM-DD'),
    to: dayjs().add(12, 'month').endOf('month').format('YYYY-MM-DD')
});

const isGlobal = (item) => item.centerId === null || item.centerId === undefined;

const DateOverridesTab = ({ centers, centersError, refreshKey, onSaved }) => {
    const [filter, setFilter] = useState(defaultFilter);
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [modal, setModal] = useState({ show: false, item: null, defaults: null });
    const [deleteState, setDeleteState] = useState({ item: null, loading: false, error: '' });
    const requestId = useRef(0);

    const loadList = useCallback(() => {
        const fallback = defaultFilter();
        const current = ++requestId.current;
        setLoading(true);
        setLoadError('');
        slotSettingActions.getDateOverrides({
            centerId: toCenterId(filter.centerId),
            from: filter.from || fallback.from,
            to: filter.to || filter.from || fallback.to
        }).then(res => {
            if (current !== requestId.current) return;
            const items = Array.isArray(res) ? res : [];
            setList([...items].sort((a, b) => (a.fromDate || '').localeCompare(b.fromDate || '')));
            setLoading(false);
        }).catch(err => {
            if (current !== requestId.current) return;
            setList([]);
            setLoading(false);
            setLoadError(getErrorMessage(err, 'Unable to load holidays and special days'));
        });
    }, [filter]);

    useEffect(() => {
        loadList();
    }, [loadList, refreshKey]);

    const isFiltered = JSON.stringify(filter) !== JSON.stringify(defaultFilter());

    const openAdd = (type) => setModal({ show: true, item: null, defaults: { type, centerId: filter.centerId } });
    const closeModal = () => setModal({ show: false, item: null, defaults: null });

    const handleSaved = (res) => {
        closeModal();
        onSaved(res);
    };

    const handleDelete = () => {
        const { item } = deleteState;
        setDeleteState({ item, loading: true, error: '' });
        slotSettingActions.deleteDateOverride(item.id).then(() => {
            setDeleteState({ item: null, loading: false, error: '' });
            toastMessage('success', `"${item.title}" deleted`);
            onSaved();
        }).catch(err => {
            setDeleteState({ item, loading: false, error: getErrorMessage(err, 'Unable to delete. Please try again.') });
        });
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
            render: title => <span className="ss-title-cell">{title}</span>
        },
        {
            title: 'Type',
            key: 'type',
            filters: [
                { text: 'Holiday / closure', value: DATE_OVERRIDE_TYPE.CLOSED },
                { text: 'Special opening', value: DATE_OVERRIDE_TYPE.OPEN }
            ],
            onFilter: (value, item) => item.type === value,
            render: (_, item) => (
                <span className={`ss-pill ${item.type === DATE_OVERRIDE_TYPE.CLOSED ? 'is-holiday' : 'is-open'}`}>{dateOverrideLabel(item)}</span>
            )
        },
        {
            title: 'Center',
            key: 'center',
            render: (_, item) => (
                <span className={`ss-pill ${isGlobal(item) ? 'is-primary' : 'is-muted'}`}>
                    {isGlobal(item) ? <FiGlobe /> : <FiMapPin />}
                    {isGlobal(item) ? 'All centers' : (item.centerName || item.centerId)}
                </span>
            )
        },
        {
            title: 'Dates',
            key: 'dates',
            defaultSortOrder: 'ascend',
            sorter: (a, b) => (a.fromDate || '').localeCompare(b.fromDate || ''),
            render: (_, item) => {
                const sameDay = !item.toDate || item.toDate === item.fromDate;
                const days = sameDay ? 1 : dayjs(item.toDate).diff(dayjs(item.fromDate), 'day') + 1;
                return (
                    <div className="ss-date-range">
                        <div>{sameDay ? formatDate(item.fromDate) : `${formatDate(item.fromDate)} – ${formatDate(item.toDate)}`}</div>
                        <div className="ss-cell-sub">{days} day{days > 1 ? 's' : ''}</div>
                    </div>
                );
            }
        },
        {
            title: 'Time',
            key: 'time',
            render: (_, item) => (item.fromTime || item.toTime
                ? <span className="ss-pill is-custom">{formatTimeRange(item.fromTime, item.toTime)}</span>
                : <span className="ss-cell-sub">Whole day</span>)
        },
        {
            title: 'Yearly',
            key: 'yearly',
            align: 'center',
            render: (_, item) => (item.isRecurringYearly
                ? <Tooltip title="Repeats every year"><span className="ss-pill is-primary"><FiRepeat /> Yearly</span></Tooltip>
                : <span className="ss-cell-sub">—</span>)
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            width: 110,
            render: (_, item) => (
                <div className="d-flex justify-content-end gap-2">
                    <Tooltip title="Edit">
                        <button type="button" className="ss-icon-btn" aria-label={`Edit ${item.title}`}
                            onClick={() => setModal({ show: true, item, defaults: null })}>
                            <FiEdit2 />
                        </button>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <button type="button" className="ss-icon-btn is-danger" aria-label={`Delete ${item.title}`}
                            onClick={() => setDeleteState({ item, loading: false, error: '' })}>
                            <FiTrash2 />
                        </button>
                    </Tooltip>
                </div>
            )
        }
    ];

    const emptyState = (
        <EmptyState
            icon={<FiFlag />}
            title={isFiltered ? 'Nothing matches these filters' : 'No holidays or special days in this period'}
            text={isFiltered ? 'Try another center or date range.' : 'Add a holiday to close centers, or a special opening to open on an off day.'}
            action={isFiltered
                ? <Button variant="outline-primary" size="sm" className="mt-2" onClick={() => setFilter(defaultFilter())}><FiX /> Reset filters</Button>
                : <Button variant="primary" size="sm" className="mt-2" onClick={() => openAdd(DATE_OVERRIDE_TYPE.CLOSED)}><FiPlus /> Add holiday</Button>}
        />
    );

    return (
        <>
            <SectionCard
                title="Filters"
                subtitle={filter.centerId
                    ? 'Showing what applies to the selected center: all-centers entries plus its own.'
                    : 'Showing every entry: all-centers ones and each center\'s own. Pick a center to see only what applies to it.'}
                actions={(
                    <div className="d-flex gap-2 flex-wrap">
                        <Button variant="primary" onClick={() => openAdd(DATE_OVERRIDE_TYPE.CLOSED)}><FiPlus /> Add holiday</Button>
                        <Button variant="success" onClick={() => openAdd(DATE_OVERRIDE_TYPE.OPEN)}><FiPlus /> Add special opening</Button>
                    </div>
                )}
            >
                {centersError && <AlertBox variant="warning" messages={`Centers could not be loaded: ${centersError}`} />}
                <div className="ss-toolbar">
                    <div>
                        <Form.Label htmlFor="do-filter-center">Center</Form.Label>
                        <CenterSelect id="do-filter-center" value={filter.centerId} centers={centers} allLabel="All centers"
                            onChange={value => setFilter(prev => ({ ...prev, centerId: value }))} />
                    </div>
                    <div>
                        <Form.Label htmlFor="do-filter-dates">Date range</Form.Label>
                        <DateRangeField id="do-filter-dates" from={filter.from} to={filter.to} allowClear={false}
                            onChange={(from, to) => setFilter(prev => ({ ...prev, from, to }))} />
                    </div>
                    <div>
                        <Button variant="light" disabled={!isFiltered} onClick={() => setFilter(defaultFilter())}><FiX /> Reset</Button>
                    </div>
                </div>
            </SectionCard>

            <SectionCard
                title="Holidays & special days"
                subtitle="Yearly entries are always listed, whatever the date filter."
                actions={!loading && !loadError && list.length > 0 && <span className="ss-pill is-muted">{list.length} {list.length === 1 ? 'entry' : 'entries'}</span>}
                bodyClassName="p-0"
            >
                {loadError ? <ErrorState message={loadError} onRetry={loadList} /> : (
                    <Table
                        className="ss-antd-table"
                        rowKey="id"
                        columns={columns}
                        dataSource={list}
                        loading={loading}
                        pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
                        scroll={{ x: 900 }}
                        locale={{ emptyText: loading ? ' ' : emptyState }}
                        showSorterTooltip={false}
                    />
                )}
            </SectionCard>

            <DateOverrideFormModal
                show={modal.show}
                item={modal.item}
                defaults={modal.defaults}
                centers={centers}
                onClose={closeModal}
                onSaved={handleSaved}
            />

            <ConfirmModal
                show={!!deleteState.item}
                title="Delete date override"
                message={deleteState.item && (
                    <>
                        Delete <strong>{deleteState.item.title}</strong> ({formatDate(deleteState.item.fromDate)}
                        {deleteState.item.toDate && deleteState.item.toDate !== deleteState.item.fromDate ? ` – ${formatDate(deleteState.item.toDate)}` : ''})?
                        Slots on these dates go back to the normal schedule.
                    </>
                )}
                confirmLabel="Delete"
                loading={deleteState.loading}
                error={deleteState.error}
                onConfirm={handleDelete}
                onClose={() => setDeleteState({ item: null, loading: false, error: '' })}
            />
        </>
    );
};

export default DateOverridesTab;
